/**
 * The payload is the thing that's in or attached to the end-effector.
 * The mass is currently useful or necessary for dynamics and planning
 * calculations.
 *
 * In the future, it will likely include a collision object as well.
 *
 * (if you're holding a widget, you don't want the widget to collide
 * with a door as you go through it)
 */
import * as zod from 'zod';

import { ModelType } from '@sb/robot';

import { Expression } from './expressions';

export const PAYLOAD_MASS_KG_DEFAULT = 0;

// Minimum payload mass (kg) allowed in the UI and by validation. Must be >= 0.
export const PAYLOAD_MASS_KG_ABSOLUTE_MIN = 0;
export const PAYLOAD_MASS_KG_ABSOLUTE_RO2_MAX = 18;
export const PAYLOAD_MASS_KG_ABSOLUTE_RO3_MAX = 30;
export const PAYLOAD_MASS_KG_ABSOLUTE_RO3_SPARK_MAX = 7;

/**
 * Get the maximum payload mass for a given robot model type.
 */
export function getMaxPayloadForModelType(modelType: ModelType): number {
  switch (modelType) {
    case ModelType.enum.Thor:
      return PAYLOAD_MASS_KG_ABSOLUTE_RO3_MAX;
    case ModelType.enum.Spark:
      return PAYLOAD_MASS_KG_ABSOLUTE_RO3_SPARK_MAX;
    default:
      return PAYLOAD_MASS_KG_ABSOLUTE_RO2_MAX;
  }
}

export const PayloadState = zod.object({
  // Clamp legacy negative mass values up to the new minimum at parse time so
  // routines saved before negative payload was disallowed still load instead
  // of failing zod validation in the editor.
  mass: zod
    .preprocess(
      (val) =>
        typeof val === 'number' && val < PAYLOAD_MASS_KG_ABSOLUTE_MIN
          ? PAYLOAD_MASS_KG_ABSOLUTE_MIN
          : val,
      zod
        .number()
        .min(PAYLOAD_MASS_KG_ABSOLUTE_MIN)
        .max(PAYLOAD_MASS_KG_ABSOLUTE_RO3_MAX),
    )
    .default(PAYLOAD_MASS_KG_DEFAULT),
  massExpression: Expression.optional(),
});

export type PayloadState = zod.infer<typeof PayloadState>;
